/**
 * Générateur — Connaître et utiliser la valeur des chiffres selon leur rang
 * Attendu ID : 596 | Niveau : 6ème | Domaine : NC
 *
 * Types générés : qcm_unique, qcm_multiple, texte, texte_trou_drag,
 *                 texte_trou_saisie, relier, classer, tableau_vf
 *
 * Labels :
 *   - Réécrire avec autant de décimales qu'un nombre de référence
 *   - Identifier le chiffre d'un rang donné
 *   - Placer la virgule selon un rang cible
 *   - Devinette — retrouver un nombre à partir d'indices sur ses rangs
 *
 * Variantes estimées : >500
 */

import {
    randomChoice, randomChoiceMultiple, shuffle, randInt,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier, makeClasser,
} from '../../../utils';

// ─── Constantes ────────────────────────────────────────────────────────────

const ATTENDU_ID = 596;

/** Options communes pour tous les payloads de cet attendu */
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Rangs et décimaux (spécifique à cet attendu) ──────────────────────────

/** Exposant de chaque rang (positif = partie entière, négatif = décimale) */
const RANG_EXP = {
    milliers: 3, centaines: 2, dizaines: 1, 'unités': 0,
    'dixièmes': -1, 'centièmes': -2, 'millièmes': -3,
};
const RANG_NOMS = Object.keys(RANG_EXP);

/** Miroir symétrique des rangs par rapport à la virgule */
const RANG_MIROIR = {
    'dixièmes': 'dizaines', 'dizaines': 'dixièmes',
    'centièmes': 'centaines', 'centaines': 'centièmes',
    'millièmes': 'milliers', 'milliers': 'millièmes',
    'unités': 'unités',
};

/** Extrait le chiffre au rang donné via manipulation de chaîne (pas de float) */
const digitAtRangStr = (numStr, rang) => {
    const s = numStr.replace(/ /g, '');
    const [intStr, decStr = ''] = s.split(',');
    const e = RANG_EXP[rang];
    if (e >= 0) {
        const pos = intStr.length - 1 - e;
        return pos >= 0 && pos < intStr.length ? parseInt(intStr[pos]) : 0;
    }
    const pos = Math.abs(e) - 1;
    return pos >= 0 && pos < decStr.length ? parseInt(decStr[pos]) : 0;
};

/** Liste des rangs présents dans l'écriture d'un nombre */
const rangsPresents = (numStr) => {
    const s = numStr.replace(/ /g, '');
    const [intStr, decStr = ''] = s.split(',');
    return RANG_NOMS.filter(rang => {
        const e = RANG_EXP[rang];
        if (e >= 0) return (intStr.length - 1 - e) >= 0;
        return (Math.abs(e) - 1) < decStr.length;
    });
};

/** Rangs adjacents (voisins dans la liste ordonnée) */
const rangAdjacent = (rang) => {
    const idx = RANG_NOMS.indexOf(rang);
    const adj = [];
    if (idx > 0) adj.push(RANG_NOMS[idx - 1]);
    if (idx < RANG_NOMS.length - 1) adj.push(RANG_NOMS[idx + 1]);
    return adj;
};

/** Ajoute des zéros pour atteindre N décimales. Ex: ("4", 3) → "4,000" */
const addTrailingZeros = (numStr, target) => {
    const s = numStr.replace(/ /g, '');
    if (s.includes(',')) {
        const [ip, dp] = s.split(',');
        if (dp.length >= target) return `${ip},${dp}`;
        return `${ip},${dp + '0'.repeat(target - dp.length)}`;
    }
    return `${s},${'0'.repeat(target)}`;
};

// ─── Pools de données ──────────────────────────────────────────────────────

/** Nombres de référence : (chaîne, nombre de décimales) */
const POOL_REFS = [
    ['4,157', 3], ['3,14', 2], ['2,050', 3],
    ['7,80', 2],  ['12,450', 3], ['9,003', 3],
    ['6,125', 3], ['1,70', 2],   ['0,6025', 4],
];

/** Nombres à réécrire, classés par nombre de décimales actuel */
const POOL_A_REECRIRE = {
    0: ['4', '12', '145', '7', '38', '200'],
    1: ['0,4', '3,1', '7,5', '0,9', '13,2', '0,7'],
    2: ['4,16', '0,75', '2,80', '0,08', '5,40'],
};

/** Nombres pour l'identification de chiffres */
const POOL_NOMBRES = [
    '84,735', '124 738,59', '6 594,273', '314,159',
    '2 047,836', '591,024', '78 362,15', '45,908', '3 216,07',
    '67,412', '953,86', '1 482,57', '706,341', '29,658',
    '8 173,042', '462,197', '5 031,68', '247,503', '1 896,24',
    '63,917', '508,263',
];

/** Pool virgule : (séquence, chiffre cible, rang cible, correct, d1, d2, d3) */
// Séquences de 6 chiffres (pas de 0 en tête, chiffres variés) — utilisées par genVirguleData
const SEQS_VIRGULE = [
    '314159', '271828', '162534', '987654', '453619',
    '527496', '836215', '649173', '738542', '195847',
    '412367', '583926', '174268', '935481', '267519',
];

/** Décalage d'un rang par rapport aux unités (position de la virgule = position_chiffre + offset) */
const RANG_OFFSET = {
    'milliers': 3, 'centaines': 2, 'dizaines': 1, 'unités': 0,
    'dixièmes': -1, 'centièmes': -2, 'millièmes': -3,
};

/** Place la virgule après la position k (1-indexé) dans une séquence */
const placerVirgule = (seq, k) => {
    if (k >= seq.length) return seq; // virgule après le dernier chiffre → nombre entier
    if (k <= 0) return `0,${seq}`;
    return seq.slice(0, k) + ',' + seq.slice(k);
};

/**
 * Génère mathématiquement un exercice de placement de virgule.
 * Retourne { seq, tgt (chiffre), rang, correct (écriture), distracteurs[3] }.
 * Zéro risque d'erreur : la position de la virgule est calculée.
 */
const genVirguleData = (seqFixe = null) => {
    const seq = seqFixe || randomChoice(SEQS_VIRGULE);
    // Rangs pertinents pour une séquence de 6 chiffres en 6ème
    const rang = randomChoice(['unités', 'dizaines', 'centaines', 'dixièmes', 'centièmes']);
    const offset = RANG_OFFSET[rang];

    // Trouver toutes les positions p où k = p + offset est valide (1 ≤ k ≤ 5)
    const positionsValides = [];
    for (let p = 1; p <= 6; p++) {
        const k = p + offset;
        if (k >= 1 && k <= 5) positionsValides.push(p);
    }
    // Éviter les chiffres en doublon dans la séquence pour que tgt soit identifiable
    const p = randomChoice(positionsValides);
    const k = p + offset;
    const tgt = seq[p - 1];
    const correct = placerVirgule(seq, k);

    // Distracteurs : virgule à d'autres positions valides (1 à 5)
    const autresK = [1, 2, 3, 4, 5].filter(x => x !== k);
    const distracteurs = randomChoiceMultiple(autresK, 3).map(x => placerVirgule(seq, x));

    return { seq, tgt, rang, correct, distracteurs };
};

// ─── Devinettes : génération dynamique (remplace l'ancien POOL_DEVINETTES) ──

/** Relations mathématiques pour l'indice "spécial" d'une devinette. */
const RELATIONS_DEVINETTE = [
    { label: 'le double', candidatsRef: [1, 2, 3, 4],  calc: (r) => 2 * r },
    { label: 'le triple', candidatsRef: [1, 2, 3],     calc: (r) => 3 * r },
    { label: 'la moitié', candidatsRef: [2, 4, 6, 8],  calc: (r) => r / 2 },
];

/** Structures de nombres possibles (4-5 rangs contigus autour de la virgule). */
const STRUCTURES_DEVINETTE = [
    ['centaines', 'dizaines', 'unités', 'dixièmes'],              // ex: 345,6
    ['dizaines', 'unités', 'dixièmes', 'centièmes'],               // ex: 34,56
    ['centaines', 'dizaines', 'unités', 'dixièmes', 'centièmes'],  // ex: 345,67
];

/** Construit la chaîne d'un nombre à partir d'un dict {rang: chiffre}. */
const buildNombreFromRangs = (digits, rangsNombre) => {
    const ordreEntier = ['milliers', 'centaines', 'dizaines', 'unités'];
    const ordreDecimal = ['dixièmes', 'centièmes', 'millièmes'];
    const entiers = ordreEntier.filter(r => rangsNombre.includes(r));
    const decimaux = ordreDecimal.filter(r => rangsNombre.includes(r));
    const partieEntiere = entiers.map(r => digits[r]).join('');
    const partieDec = decimaux.map(r => digits[r]).join('');
    return partieDec ? `${partieEntiere},${partieDec}` : partieEntiere;
};

/** Génère 3 distracteurs plausibles par permutation des chiffres. */
const genDistracteursPermutation = (answer) => {
    const [intPart, decPart = ''] = answer.split(',');
    const distracts = new Set();
    let tries = 0;
    while (distracts.size < 3 && tries++ < 30) {
        const chiffres = (intPart + decPart).split('');
        const shuffled = shuffle([...chiffres]);
        const newInt = shuffled.slice(0, intPart.length).join('');
        const newDec = shuffled.slice(intPart.length).join('');
        const candidate = newDec ? `${newInt},${newDec}` : newInt;
        // Accepter si différent de la réponse, et soit commence par un chiffre non-zéro, soit commence par "0,"
        if (candidate !== answer && (!candidate.startsWith('0') || candidate.startsWith('0,'))) {
            distracts.add(candidate);
        }
    }
    return [...distracts].slice(0, 3);
};

/**
 * Génère dynamiquement une devinette valide.
 * 3 indices directs + 1 indice relation (double/triple/moitié).
 * Retourne { answer, clues[4], distractors[3] }.
 */
const genDevinetteData = () => {
    const rangsNombre = randomChoice(STRUCTURES_DEVINETTE);

    // Choisir le rang "relation" et le rang de référence (parmi rangsNombre)
    const rangRelation = randomChoice(rangsNombre);
    const autresRangs = rangsNombre.filter(r => r !== rangRelation);
    const rangRef = randomChoice(autresRangs);

    // Tirer la relation + les valeurs
    const relation = randomChoice(RELATIONS_DEVINETTE);
    const valRef = randomChoice(relation.candidatsRef);
    const valRelation = relation.calc(valRef);

    // Tirer les valeurs pour les autres rangs (distinctes pour éviter ambiguïté)
    const digits = { [rangRef]: valRef, [rangRelation]: valRelation };
    const used = new Set([valRef, valRelation]);
    const rangsRestants = autresRangs.filter(r => r !== rangRef);
    for (const r of rangsRestants) {
        let v, tries = 0;
        do { v = randInt(0, 9); tries++; } while (used.has(v) && tries < 20);
        digits[r] = v;
        used.add(v);
    }

    // Interdire 0 en tête de la partie entière — protéger rangRef et rangRelation
    // (sinon la relation mathématique serait cassée)
    const premierRang = rangsNombre.find(r => ['milliers', 'centaines', 'dizaines', 'unités'].includes(r));
    if (digits[premierRang] === 0) {
        // Chercher un rang "libre" (ni rangRef, ni rangRelation, ni premierRang) avec valeur non nulle
        const swap = Object.entries(digits).find(([r, v]) =>
            r !== rangRef && r !== rangRelation && r !== premierRang && v !== 0
        );
        if (swap) {
            digits[premierRang] = swap[1];
            digits[swap[0]] = 0;
        }
        // Sinon on laisse le 0 en tête (cas extrême rarissime)
    }

    const answer = buildNombreFromRangs(digits, rangsNombre);

    // 3 indices directs : les 3 rangs qui NE sont PAS le rang relation
    const rangsDirects = autresRangs;
    const cluesDirectes = rangsDirects.map(r => `Mon chiffre des ${r} est ${digits[r]}.`);
    const clueRelation = `Mon chiffre des ${rangRelation} est ${relation.label} de mon chiffre des ${rangRef}.`;

    return {
        answer,
        clues: shuffle([...cluesDirectes, clueRelation]),
        distractors: genDistracteursPermutation(answer),
    };
};


// ─── Helpers spécifiques à cet attendu ─────────────────────────────────────

/**
 * Génère des distracteurs pour l'exercice "réécrire avec N décimales".
 * Produit des erreurs typiques : trop/pas assez de zéros, mauvais placement.
 */
const genReecrireDistractors = (numStr, target) => {
    const s = numStr.replace(/ /g, '');
    const distractors = [];

    if (s.includes(',')) {
        const [ip, dp] = s.split(',');
        const z = target - dp.length;
        if (z >= 1) {
            // D1 : un zéro de trop peu
            distractors.push(`${ip},${dp + '0'.repeat(Math.max(0, z - 1))}`);
            // D2 : zéros mal placés (avant les chiffres décimaux)
            if (z >= 2) {
                const zb = Math.floor(z / 2);
                const za = z - zb;
                distractors.push(`${ip},${'0'.repeat(zb)}${dp}${'0'.repeat(za)}`);
            } else {
                distractors.push(`${ip},0${dp}`);
            }
        }
        // D3 : un zéro de trop
        distractors.push(`${ip},${dp + '0'.repeat(z + 1)}`);
    } else {
        const z = target;
        distractors.push(`${s},${'0'.repeat(Math.max(1, z - 1))}`); // D1 : pas assez
        distractors.push(`${s}${'0'.repeat(z)}`);                   // D2 : virgule oubliée
        distractors.push(`${s},${'0'.repeat(z + 1)}`);              // D3 : trop
    }

    // Dédupliquer et exclure la bonne réponse
    const correct = addTrailingZeros(numStr, target);
    const seen = new Set([correct]);
    return distractors.filter(d => {
        if (seen.has(d)) return false;
        seen.add(d);
        return true;
    }).slice(0, 3);
};

/**
 * Choisit un nombre et un rang non nul présent dans ce nombre.
 * Retourne [numStr, rang, chiffreCorrect].
 */
const pickNombreEtRang = () => {
    for (let i = 0; i < 20; i++) {
        const numStr = randomChoice(POOL_NOMBRES);
        const rp = rangsPresents(numStr);
        if (rp.length >= 4) {
            const rang = randomChoice(rp);
            const correct = digitAtRangStr(numStr, rang);
            if (correct !== 0) return [numStr, rang, correct];
        }
    }
    return ['84,735', 'centièmes', 3]; // fallback
};

// ─── EXERCICE 1 : Réécrire avec autant de décimales ───────────────────────

const L1 = 'Réécrire avec autant de décimales qu\'un nombre de référence';

const genReecrireQcm = () => {
    const [refStr, N] = randomChoice(POOL_REFS);
    const m = randomChoice(Object.keys(POOL_A_REECRIRE).filter(k => parseInt(k) < N).map(Number));
    const numStr = randomChoice(POOL_A_REECRIRE[m]);
    const correct = addTrailingZeros(numStr, N);
    const distractors = genReecrireDistractors(numStr, N);
    // S'assurer d'avoir 4 choix uniques
    const allC = [...new Set([correct, ...distractors])].slice(0, 4);
    while (allC.length < 4) allC.push(addTrailingZeros(numStr, N + allC.length));
    const texte = `<p>Réécris le nombre <strong>${numStr}</strong> pour qu'il comporte autant de décimales que le nombre ${refStr}.</p>`;
    return makeQcm(allC, correct, texte, opts(L1));
};

const genReecrireTexte = () => {
    const [refStr, N] = randomChoice(POOL_REFS);
    const m = randomChoice(Object.keys(POOL_A_REECRIRE).filter(k => parseInt(k) < N).map(Number));
    const numStr = randomChoice(POOL_A_REECRIRE[m]);
    const correct = addTrailingZeros(numStr, N);
    const texte = `<p>Réécris le nombre <strong>${numStr}</strong> pour qu'il comporte autant de décimales que le nombre ${refStr}.</p>`;
    return makeSaisie(correct, texte, opts(L1));
};

const genReecrireDrag = () => {
    const [refStr, N] = randomChoice(POOL_REFS);
    const candidates = Object.keys(POOL_A_REECRIRE).filter(k => parseInt(k) < N).flatMap(k => POOL_A_REECRIRE[k]);
    const [aStr, bStr] = randomChoiceMultiple(candidates, 2);
    const ca = addTrailingZeros(aStr, N);
    const cb = addTrailingZeros(bStr, N);
    const phrase = `${aStr} = {{${ca}}} \u00a0\u00a0 ${bStr} = {{${cb}}}`;
    const da = genReecrireDistractors(aStr, N);
    const db = genReecrireDistractors(bStr, N);
    const distracteurs = [...new Set([...da.slice(0, 1), ...db.slice(0, 1)])];
    const texte = `<p>Glisse les bons nombres pour compléter chaque égalité. Le nombre de référence est <strong>${refStr}</strong> (${N} décimales).</p>`;
    return makeTexteTrou(phrase, texte, { ...opts(L1), distracteurs });
};

const genReecrireSaisie = () => {
    const [refStr, N] = randomChoice(POOL_REFS);
    const aStr = randomChoice(POOL_A_REECRIRE[0]); // entier
    const m = randomChoice(Object.keys(POOL_A_REECRIRE).filter(k => { const ki = parseInt(k); return ki > 0 && ki < N; }).map(Number));
    const bStr = randomChoice(POOL_A_REECRIRE[m]);
    const ca = addTrailingZeros(aStr, N);
    const cb = addTrailingZeros(bStr, N);
    const phrase = `${aStr} = {{${ca}}} \u00a0\u00a0 ${bStr} = {{${cb}}}`;
    const texte = `<p>Complète chaque égalité pour que le nombre ait autant de décimales que ${refStr}.</p>`;
    return makeTexteTrou(phrase, texte, { ...opts(L1), mode: 'saisie' });
};

const genReecrireRelier = () => {
    const [refStr, N] = randomChoice(POOL_REFS);
    const candidates = Object.keys(POOL_A_REECRIRE).filter(k => parseInt(k) < N).flatMap(k => POOL_A_REECRIRE[k]);
    const selected = randomChoiceMultiple(candidates, 4);
    const pairs = selected.map(n => ({ source: n, target: addTrailingZeros(n, N) }));
    const texte = `<p>Relie chaque nombre à son écriture comportant <strong>${N} décimale${N > 1 ? 's' : ''}</strong> (comme ${refStr}).</p>`;
    return makeRelier(pairs, texte, opts(L1));
};

const genReecrireTableauVf = () => {
    const [refStr, N] = randomChoice(POOL_REFS);
    const n1 = randomChoice(POOL_A_REECRIRE[1]);
    const c1 = addTrailingZeros(n1, N);
    const n2 = randomChoice(POOL_A_REECRIRE[0]);
    const c2 = addTrailingZeros(n2, N);
    const wrong1 = addTrailingZeros(n1, N - 1);
    const lignes = [
        `${n1} et ${c1} représentent le même nombre`,
        'Ajouter des zéros après le dernier chiffre décimal change la valeur du nombre',
        `${n2} s'écrit ${c2} avec ${N} décimale${N > 1 ? 's' : ''}`,
        `${n1} s'écrit ${wrong1} avec ${N} décimale${N > 1 ? 's' : ''}`,
    ];
    const bonnesReponses = [0, 1, 0, 1]; // Vrai, Faux, Vrai, Faux
    const texte = '<p>Indique si chaque affirmation est vraie ou fausse.</p>';
    return makeVraiFaux(lignes, bonnesReponses, texte, opts(L1));
};

const genReecrireClasser = () => {
    const [refStr, N] = randomChoice(POOL_REFS);
    let categories = [];
    for (let zeros = 1; zeros <= N; zeros++) {
        const mSrc = N - zeros;
        if (POOL_A_REECRIRE[mSrc]) {
            const items = randomChoiceMultiple(POOL_A_REECRIRE[mSrc], Math.min(3, POOL_A_REECRIRE[mSrc].length));
            categories.push({ name: `Ajouter ${zeros} zéro${zeros > 1 ? 's' : ''}`, items });
        }
    }
    if (categories.length < 2) {
        categories = [
            { name: 'Ajouter 1 zéro', items: randomChoiceMultiple(POOL_A_REECRIRE[N - 1] || POOL_A_REECRIRE[1], 2) },
            { name: 'Ajouter 2 zéros', items: randomChoiceMultiple(POOL_A_REECRIRE[N - 2] || POOL_A_REECRIRE[0], 2) },
        ];
    }
    const texte = `<p>Classe ces nombres selon le nombre de zéros à ajouter <strong>dans la partie décimale</strong> pour obtenir une écriture à ${N} décimales.</p>`;
    return makeClasser(categories, texte, opts(L1));
};

// ─── EXERCICE 2 : Identifier le chiffre d'un rang ─────────────────────────

const L2 = 'Identifier le chiffre d\'un rang donné';

const genIdentifierQcm = () => {
    const [numStr, rang, correct] = pickNombreEtRang();
    const correctS = String(correct);
    const choices = [correctS];

    // Candidat 2 : chiffre du rang miroir, s'il est différent de la bonne réponse
    const miroirDigit = String(digitAtRangStr(numStr, RANG_MIROIR[rang]));
    if (miroirDigit !== correctS) choices.push(miroirDigit);

    // Compléter avec d'autres chiffres distincts du nombre
    const allDigits = rangsPresents(numStr).map(r => String(digitAtRangStr(numStr, r)));
    for (const d of allDigits) {
        if (choices.length >= 4) break;
        if (!choices.includes(d)) choices.push(d);
    }

    const texte = `<p>Dans le nombre <strong>${numStr}</strong>, quel est le chiffre des <strong>${rang}</strong> ?</p>`;
    return makeQcm(choices, correctS, texte, opts(L2));
};

const genIdentifierQcmMulti = () => {
    let numStr = randomChoice(POOL_NOMBRES);
    const allRp = rangsPresents(numStr);
    const decDigits = allRp.filter(r => RANG_EXP[r] < 0).map(r => String(digitAtRangStr(numStr, r)));
    const intDigits = allRp.filter(r => RANG_EXP[r] >= 0).map(r => String(digitAtRangStr(numStr, r)));
    const choicesCorrect = randomChoiceMultiple(decDigits, Math.min(3, decDigits.length));
    const choicesWrong = randomChoiceMultiple(intDigits, Math.min(2, intDigits.length));
    let allC = [...new Set([...choicesCorrect, ...choicesWrong])];
    if (allC.length < 4) {
        const extras = Array.from({ length: 10 }, (_, i) => String(i)).filter(d => !allC.includes(d));
        allC = [...allC, ...extras.slice(0, 4 - allC.length)];
    }
    allC = allC.slice(0, 5);
    const texte = `<p>Dans le nombre <strong>${numStr}</strong>, quels chiffres appartiennent à la <strong>partie décimale</strong> ? (Plusieurs réponses possibles)</p>`;
    return makeQcm(allC, choicesCorrect, texte, opts(L2), true);
};

const genIdentifierTexte = () => {
    const [numStr, rang, correct] = pickNombreEtRang();
    const texte = `<p>Dans le nombre <strong>${numStr}</strong>, quel est le chiffre des <strong>${rang}</strong> ?</p>`;
    return makeSaisie(String(correct), texte, { ...opts(L2), strict: true });
};

const genIdentifierDrag = () => {
    let numStr = randomChoice(POOL_NOMBRES);
    let rangsDec = rangsPresents(numStr).filter(r => RANG_EXP[r] < 0);
    if (rangsDec.length < 3) { numStr = '84,735'; rangsDec = ['dixièmes', 'centièmes', 'millièmes']; }
    const threeRangs = shuffle([...rangsDec]).slice(0, 3);
    const phrase = threeRangs.map(r => `Le chiffre des ${r} est {{${digitAtRangStr(numStr, r)}}}.`).join(' ');
    // Distracteurs : chiffres de la partie entière, en EXCLUANT les chiffres
    // attendus dans les trous (un distracteur égal à un bon mot = deux bonnes
    // réponses au scoring). Complément par des chiffres absents si besoin.
    const motsTrous = new Set(threeRangs.map(r => String(digitAtRangStr(numStr, r))));
    const intRangs = rangsPresents(numStr).filter(r => RANG_EXP[r] >= 0);
    const distracteurs = [...new Set(
        shuffle([...intRangs]).map(r => String(digitAtRangStr(numStr, r))),
    )].filter(d => !motsTrous.has(d)).slice(0, 2);
    for (let d = 0; distracteurs.length < 2 && d <= 9; d++) {
        const s = String(d);
        if (!motsTrous.has(s) && !distracteurs.includes(s)) distracteurs.push(s);
    }
    const texte = `<p>Dans le nombre <strong>${numStr}</strong>, glisse le bon chiffre dans chaque trou.</p>`;
    return makeTexteTrou(phrase, texte, { ...opts(L2), distracteurs });
};

const genIdentifierSaisie = () => {
    let numStr = randomChoice(POOL_NOMBRES);
    let rp = rangsPresents(numStr);
    if (rp.length < 3) { numStr = '124 738,59'; rp = rangsPresents(numStr); }
    const threeRangs = randomChoiceMultiple(rp, 3);
    const phrase = threeRangs.map(r => `Le chiffre des ${r} est {{${digitAtRangStr(numStr, r)}}}.`).join(' ');
    const texte = `<p>Dans le nombre <strong>${numStr}</strong>, complète.</p>`;
    return makeTexteTrou(phrase, texte, { ...opts(L2), mode: 'saisie', strict: true });
};

const genIdentifierRelier = () => {
    // 4 rangs dont les CHIFFRES sont tous distincts : deux rangs portant le même
    // chiffre donneraient deux cibles identiques à relier (appariement indécidable).
    let numStr = null;
    let fourRangs = null;
    for (let attempt = 0; attempt < 30 && !fourRangs; attempt++) {
        const candidat = randomChoice(POOL_NOMBRES);
        const rp = rangsPresents(candidat);
        if (rp.length < 4) continue;
        const sel = [];
        const chiffresVus = new Set();
        for (const r of shuffle([...rp])) {
            const d = String(digitAtRangStr(candidat, r));
            if (!chiffresVus.has(d)) {
                chiffresVus.add(d);
                sel.push(r);
            }
            if (sel.length === 4) break;
        }
        if (sel.length === 4) {
            numStr = candidat;
            fourRangs = sel;
        }
    }
    if (!fourRangs) {
        numStr = '84,735'; // chiffres tous distincts
        fourRangs = ['dixièmes', 'centièmes', 'millièmes', 'unités'];
    }
    const pairs = fourRangs.map(r => ({
        source: `Chiffre des ${r}`,
        target: String(digitAtRangStr(numStr, r)),
    }));
    const texte = `<p>Dans le nombre <strong>${numStr}</strong>, relie chaque rang au chiffre qui lui correspond.</p>`;
    return makeRelier(pairs, texte, opts(L2));
};

const genIdentifierTableauVf = () => {
    // Tirer un nombre avec au moins 4 rangs ayant des chiffres non nuls distincts
    let numStr, fourRangs, digits;
    for (let attempt = 0; attempt < 30; attempt++) {
        numStr = randomChoice(POOL_NOMBRES);
        const rp = rangsPresents(numStr);
        // Garder les rangs dont le chiffre est non nul
        const nonZero = rp.filter(r => digitAtRangStr(numStr, r) !== 0);
        if (nonZero.length < 4) continue;
        // Tirer 4 rangs distincts
        fourRangs = shuffle([...nonZero]).slice(0, 4);
        digits = fourRangs.map(r => digitAtRangStr(numStr, r));
        // Vérifier qu'on a au moins 2 chiffres distincts (pour que les faux soient bien faux)
        if (new Set(digits).size >= 2) break;
    }
    if (!fourRangs) {
        // Fallback sûr
        numStr = '84,735'; fourRangs = ['dizaines', 'unités', 'dixièmes', 'centièmes'];
        digits = fourRangs.map(r => digitAtRangStr(numStr, r));
    }

    // 2 VRAI (bon chiffre au bon rang) + 2 FAUX (chiffre d'un autre rang)
    const lignes = [];
    const bonnesReponses = [];

    // Lignes 1-2 : VRAI — le bon chiffre au bon rang
    lignes.push(`Le chiffre des ${fourRangs[0]} est ${digits[0]}`);
    bonnesReponses.push(0);
    lignes.push(`Le chiffre des ${fourRangs[1]} est ${digits[1]}`);
    bonnesReponses.push(0);

    // Lignes 3-4 : FAUX — un chiffre qui existe dans le nombre mais au mauvais rang
    // Pour le rang 2, on colle le chiffre du rang 0 (qui est différent si possible)
    const faux3 = digits[0] !== digits[2] ? digits[0] : digits[1];
    lignes.push(`Le chiffre des ${fourRangs[2]} est ${faux3}`);
    bonnesReponses.push(1);
    // Pour le rang 3, on colle le chiffre du rang 1
    const faux4 = digits[1] !== digits[3] ? digits[1] : digits[0];
    lignes.push(`Le chiffre des ${fourRangs[3]} est ${faux4}`);
    bonnesReponses.push(1);

    // Mélanger l'ordre pour que ce ne soit pas toujours VV FF
    const indices = shuffle([0, 1, 2, 3]);
    const texte = `<p>Dans le nombre <strong>${numStr}</strong>, indique si chaque affirmation est vraie ou fausse.</p>`;
    return makeVraiFaux(
        indices.map(i => lignes[i]),
        indices.map(i => bonnesReponses[i]),
        texte, opts(L2)
    );
};

const genIdentifierClasser = () => {
    let numStr = randomChoice(POOL_NOMBRES);
    // Extraire tous les chiffres depuis la chaîne (bypass rangsPresents qui est limité à 7 rangs)
    const cleaned = numStr.replace(/ /g, '');
    const [intStr, decStr = ''] = cleaned.split(',');
    let intDigits = intStr.split('');
    let decDigits = decStr.split('');
    // Fallback défensif : si le pool évolue avec un nombre sans virgule ou sans partie entière
    if (!decDigits.length || !intDigits.length) {
        numStr = '6 594,273';
        intDigits = ['6', '5', '9', '4'];
        decDigits = ['2', '7', '3'];
    }
    const categories = [
        { name: 'Partie entière', items: intDigits },
        { name: 'Partie décimale', items: decDigits },
    ];
    const texte = `<p>Dans le nombre <strong>${numStr}</strong>, classe chaque chiffre selon sa position.</p>`;
    return makeClasser(categories, texte, opts(L2));
};

// ─── EXERCICE 3 : Placer la virgule selon un rang cible ───────────────────

const L3 = 'Placer la virgule selon un rang cible';

const genVirguleQcm = () => {
    const { seq, tgt, rang, correct, distracteurs } = genVirguleData();
    const seqSpaced = seq.split('').join(' ');
    const texte = `<p>Place la virgule dans les chiffres <strong>${seqSpaced}</strong> pour que <strong>${tgt}</strong> soit le chiffre des <strong>${rang}</strong>.</p>`;
    return makeQcm([correct, ...distracteurs], correct, texte, opts(L3));
};

const genVirguleTexte = () => {
    const { seq, tgt, rang, correct } = genVirguleData();
    const seqSpaced = seq.split('').join(' ');
    const texte = `<p>Place la virgule dans les chiffres <strong>${seqSpaced}</strong> pour que <strong>${tgt}</strong> soit le chiffre des <strong>${rang}</strong>. Écris le nombre obtenu.</p>`;
    return makeSaisie(correct, texte, opts(L3));
};

const genVirguleSaisie = () => {
    // 2 conditions sur la MÊME séquence, rangs différents
    const d1 = genVirguleData();
    let d2 = genVirguleData(d1.seq);
    let tries = 0;
    while (d2.rang === d1.rang && tries++ < 10) d2 = genVirguleData(d1.seq);
    const seqSpaced = d1.seq.split('').join(' ');
    const phrase = `Pour que ${d1.tgt} soit aux ${d1.rang} : {{${d1.correct}}} — Pour que ${d2.tgt} soit aux ${d2.rang} : {{${d2.correct}}}`;
    const texte = `<p>Avec les chiffres <strong>${seqSpaced}</strong>, complète selon chaque condition.</p>`;
    return makeTexteTrou(phrase, texte, { ...opts(L3), mode: 'saisie' });
};

const genVirguleRelier = () => {
    // 3 conditions sur la MÊME séquence, rangs distincts ET résultats distincts
    // (deux conditions différentes peuvent placer la virgule au même endroit →
    // deux cibles identiques à relier = appariement indécidable pour l'élève)
    const first = genVirguleData();
    const pairs = [{ source: `${first.tgt} est le chiffre des ${first.rang}`, target: first.correct }];
    const usedRangs = new Set([first.rang]);
    const usedTargets = new Set([first.correct]);
    let tries = 0;
    while (pairs.length < 3 && tries++ < 30) {
        const d = genVirguleData(first.seq);
        if (!usedRangs.has(d.rang) && !usedTargets.has(d.correct)) {
            pairs.push({ source: `${d.tgt} est le chiffre des ${d.rang}`, target: d.correct });
            usedRangs.add(d.rang);
            usedTargets.add(d.correct);
        }
    }
    const seqSpaced = first.seq.split('').join(' ');
    const texte = `<p>Relie chaque condition à l'écriture correcte des chiffres <strong>${seqSpaced}</strong>.</p>`;
    return makeRelier(pairs, texte, opts(L3));
};

const genVirguleTableauVf = () => {
    const { correct } = genVirguleData();
    let rp = rangsPresents(correct);
    if (rp.length < 4) rp = [...rp, 'dixièmes', 'unités', 'dizaines'].slice(0, 4);
    const fourRangs = rp.slice(0, 4);
    const lignes = fourRangs.map(r => `${digitAtRangStr(correct, r)} est le chiffre des ${r}`);
    const bonnesReponses = fourRangs.map(() => 0); // tous Vrai initialement
    // Rendre 2 affirmations fausses en échangeant avec un rang adjacent
    const idxToFlip = randomChoiceMultiple(fourRangs.map((_, i) => i), 2);
    idxToFlip.forEach(i => {
        const r = fourRangs[i];
        const adj = rangAdjacent(r);
        const wrongR = adj.length ? randomChoice(adj) : RANG_MIROIR[r];
        lignes[i] = `${digitAtRangStr(correct, wrongR)} est le chiffre des ${r}`;
        bonnesReponses[i] = 1; // Faux
    });
    const texte = `<p>Indique si chaque affirmation est vraie ou fausse pour le nombre <strong>${correct}</strong>.</p>`;
    return makeVraiFaux(lignes, bonnesReponses, texte, opts(L3));
};

// ─── EXERCICE 4 : Devinette ───────────────────────────────────────────────

const L4 = 'Devinette — retrouver un nombre à partir d\'indices sur ses rangs';

const genDevinetteQcm = () => {
    const dev = genDevinetteData();
    const texte = `<p>${dev.clues.join(' ')} Qui suis-je ?</p>`;
    const choices = [dev.answer, ...dev.distractors];
    return makeQcm(choices, dev.answer, texte, opts(L4));
};

const genDevinetteTexte = () => {
    const dev = genDevinetteData();
    const texte = `<p>${dev.clues.join(' ')} Quel est ce nombre ?</p>`;
    return makeSaisie(dev.answer, texte, opts(L4));
};

const genDevinetteTableauVf = () => {
    const dev = genDevinetteData();
    const numStr = dev.answer;
    let rp = rangsPresents(numStr);
    if (rp.length < 4) rp = [...rp, 'dixièmes', 'unités'].slice(0, 4);
    const fourRangs = rp.slice(0, 4);
    const lignes = fourRangs.map(r => `Le chiffre des ${r} est ${digitAtRangStr(numStr, r)}`);
    const bonnesReponses = fourRangs.map(() => 0); // tous Vrai
    // Rendre 2 fausses
    const idxToFlip = randomChoiceMultiple(fourRangs.map((_, i) => i), 2);
    idxToFlip.forEach(i => {
        const r = fourRangs[i];
        const adj = rangAdjacent(r);
        const wrongR = adj.length ? randomChoice(adj) : RANG_MIROIR[r];
        lignes[i] = `Le chiffre des ${r} est ${digitAtRangStr(numStr, wrongR)}`;
        bonnesReponses[i] = 1; // Faux
    });
    const texte = `<p>Le nombre mystère est <strong>${numStr}</strong>. Indique si chaque affirmation est vraie ou fausse.</p>`;
    return makeVraiFaux(lignes, bonnesReponses, texte, opts(L4));
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    reecrire_qcm_unique:        genReecrireQcm,
    reecrire_texte:             genReecrireTexte,
    reecrire_texte_trou_drag:   genReecrireDrag,
    reecrire_texte_trou_saisie: genReecrireSaisie,
    reecrire_relier:            genReecrireRelier,
    reecrire_tableau_vf:        genReecrireTableauVf,
    reecrire_classer:           genReecrireClasser,
    identifier_qcm_unique:      genIdentifierQcm,
    identifier_qcm_multiple:    genIdentifierQcmMulti,
    identifier_texte:           genIdentifierTexte,
    identifier_texte_trou_drag: genIdentifierDrag,
    identifier_texte_trou_saisie: genIdentifierSaisie,
    identifier_relier:          genIdentifierRelier,
    identifier_tableau_vf:      genIdentifierTableauVf,
    identifier_classer:         genIdentifierClasser,
    virgule_qcm_unique:         genVirguleQcm,
    virgule_texte:              genVirguleTexte,
    virgule_texte_trou_saisie:  genVirguleSaisie,
    virgule_relier:             genVirguleRelier,
    virgule_tableau_vf:         genVirguleTableauVf,
    devinette_qcm_unique:       genDevinetteQcm,
    devinette_texte:            genDevinetteTexte,
    devinette_tableau_vf:       genDevinetteTableauVf,
};

/**
 * Extrait les noms de format lisibles depuis les clés du registre.
 * Ex: "reecrire_qcm_unique" → "QCM", "reecrire_texte_trou_drag" → "Drag"
 */
const FORMAT_LABELS = {
    qcm_unique: 'QCM',
    qcm_multiple: 'QCM Multi',
    texte: 'Saisie',
    texte_trou_drag: 'Drag',
    texte_trou_saisie: 'Saisie trous',
    relier: 'Relier',
    tableau_vf: 'Vrai/Faux',
    classer: 'Classer',
};

/** Construit la liste des formats pour une catégorie (préfixe) */
const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter(k => k.startsWith(`${prefix}_`))
        .map(k => {
            const suffix = k.slice(prefix.length + 1); // ex: "qcm_unique"
            return { key: k, label: FORMAT_LABELS[suffix] || suffix };
        });

/**
 * Catégories d'exercices — chaque catégorie = un ExerciseurCard.
 * id = préfixe, label = titre affiché, formatsList = [{key, label}] pour les pills.
 */
export const CATEGORIES = [
    { id: 'reecrire', label: L1, formatsList: buildFormats('reecrire') },
    { id: 'identifier', label: L2, formatsList: buildFormats('identifier') },
    { id: 'virgule', label: L3, formatsList: buildFormats('virgule') },
    { id: 'devinette', label: L4, formatsList: buildFormats('devinette') },
];

/**
 * Point d'entrée : génère un exercice aléatoire pour l'attendu 596.
 * @param {string} [typeExercice] - Clé spécifique (ex: "identifier_qcm_unique"), catégorie (ex: "identifier"), ou null pour aléatoire
 * @returns {Object} Payload au format BanqueQuestion
 */
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        // Recherche par catégorie ou partielle (ex: "identifier" → tous les identifier_*)
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
